import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, LogIn, Plug, ShieldCheck, Store } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { connectAuthorize } from "../../../api/integrations";
import { AuthService } from "../../../services/authservice";
import { iconList } from "../../../utils/iconList";
import { rememberConnectReturn } from "../../../utils/connectReturn";
import useAuth from "../../../hooks/useAuth";
import "./ModernIntegrations.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Pantalla de autorización del connect-flow: el plugin de la tienda (Woo/PS)
// redirige aquí con ?platform=&shop=&state=&callback=. El comercio aprueba con
// su sesión del portal; llamamos connect/authorize (crea/reclama la
// integración con la URL REAL que reportó el plugin y emite un código
// one-time) y devolvemos el navegador al callback del plugin con state+code.
// El plugin canjea ese código servidor-a-servidor por la clave — la clave
// nunca pasa por URLs del navegador.
//
// Seguridad en esta capa: plataforma whitelisteada, callback restringido (ver
// isCallbackAllowed) y state opaco que se devuelve tal cual (anti-CSRF del
// lado del plugin).
//
// Shopify entra por el mismo flujo pero con una topología distinta: no es un
// plugin que corre DENTRO de la tienda, sino una app embebida alojada por
// nosotros. Su callback vive en el host de la app (shop.beartrackapp.com), no
// en el de la tienda (x.myshopify.com), así que la regla "mismo host" no
// aplica y se sustituye por un allowlist explícito SOLO para esa plataforma.
// ─────────────────────────────────────────────────────────────────────────────

const PLATFORMS = {
  woocommerce: { label: "WooCommerce", icon: "woo" },
  prestashop: { label: "PrestaShop", icon: "prestashop" },
  shopify: { label: "Shopify", icon: "shopify" },
};

/**
 * Hosts permitidos como callback de Shopify. Configurable por entorno para no
 * atar el bundle a un dominio; el valor por defecto es el `application_url`
 * declarado en shopify.app.toml, que es público de todos modos (figura en la
 * ficha del App Store). Se prefiere ese fallback a fallar en silencio si la
 * variable no está definida en el build.
 */
const SHOPIFY_CALLBACK_HOSTS = (
  process.env.REACT_APP_SHOPIFY_CONNECT_CALLBACK_HOSTS ||
  "shop.beartrackapp.com"
)
  .split(",")
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

function parseHttpUrl(raw) {
  try {
    const url = new URL(String(raw));
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url;
  } catch {
    return null;
  }
}

/**
 * Anti open-redirect, por plataforma.
 *
 * - Woo/PrestaShop: el retorno DEBE vivir en la misma tienda que pide
 *   conectarse (regla original, intacta).
 * - Shopify: la tienda debe ser un *.myshopify.com legítimo y el retorno debe
 *   apuntar a un host nuestro del allowlist.
 */
export function isCallbackAllowed(platform, shopUrl, callbackUrl) {
  if (platform === "shopify") {
    const isShopifyStore = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(
      shopUrl.host.toLowerCase(),
    );
    return (
      isShopifyStore &&
      SHOPIFY_CALLBACK_HOSTS.includes(callbackUrl.host.toLowerCase())
    );
  }
  return callbackUrl.host === shopUrl.host;
}

/**
 * Cuerpo de connect/authorize. Shopify se identifica por dominio de tienda
 * (host pelado, que es lo que el backend persiste como sender_id y lo que
 * SenderIdGuard compara por igualdad exacta); Woo/PrestaShop por URL completa.
 */
export function authorizeBody(platform, shopUrl) {
  if (platform === "shopify") {
    return { shopDomain: shopUrl.host.toLowerCase() };
  }
  return {
    url: shopUrl.origin + shopUrl.pathname.replace(/\/+$/, ""),
  };
}

/**
 * ¿La sesión es de una cuenta de comercio? Solo esas pueden autorizar: la
 * integración se crea a nombre de SU empresa, y una cuenta sin empresa recibe
 * 403 del backend. Esto es UX (no ofrecer un botón que va a fallar); la
 * compuerta real es el backend.
 *
 * `user` es el de useAuth: `role` llega como _id y el tipo en `roleType` (ver
 * authProviders); `companyId` es el _id de la empresa, como string.
 */
export function isCommerceAccount(user) {
  const companyId = user?.companyId;
  return (
    user?.roleType === "company" &&
    typeof companyId === "string" &&
    companyId.length > 0
  );
}

export default function ConnectStoreView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [params] = useSearchParams();
  const [authorizing, setAuthorizing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // La vinculación entrega una credencial de ESTA cuenta a un host que declara
  // la propia solicitud (ver isCallbackAllowed: para Woo/PrestaShop `shop` y
  // `callback` los pone quien arma el link, así que la regla "mismo host" se
  // cumple sola si los dos son del atacante). El único control que le queda al
  // portal es que el comercio reconozca ese dominio a conciencia, así que el
  // botón de autorizar no se habilita hasta que lo confirme.
  const [hostConfirmed, setHostConfirmed] = useState(false);

  const request = useMemo(() => {
    const platform = String(params.get("platform") || "").toLowerCase();
    const shopUrl = parseHttpUrl(params.get("shop"));
    const callbackUrl = parseHttpUrl(params.get("callback"));
    const state = String(params.get("state") || "");

    if (!PLATFORMS[platform]) return { error: t("Plataforma no soportada.") };
    if (!shopUrl) return { error: t("URL de tienda inválida.") };
    if (!callbackUrl || !isCallbackAllowed(platform, shopUrl, callbackUrl)) {
      return { error: t("El callback no pertenece a la tienda.") };
    }
    if (!state) return { error: t("Solicitud incompleta (falta state).") };

    return { platform, shopUrl, callbackUrl, state };
  }, [params, t]);

  // Si cambian los parámetros (otra tienda) sin remontar la vista, el
  // consentimiento anterior no vale: se vuelve a pedir.
  useEffect(() => {
    setHostConfirmed(false);
  }, [params]);

  const authorize = async () => {
    if (authorizing || request.error || !hostConfirmed) return;
    setAuthorizing(true);
    setErrorMessage("");
    try {
      const res = await connectAuthorize(
        request.platform,
        authorizeBody(request.platform, request.shopUrl),
      );
      const code = res?.data?.code;
      if (res?.error?.statusCode === 409) {
        setErrorMessage(
          t("Esta tienda ya está vinculada a otra cuenta Beartrack. Si es tuya, contacta a soporte para liberarla."),
        );
      } else if (res?.error?.statusCode === 403) {
        setErrorMessage(t("Solo una cuenta de comercio puede autorizar esta conexión."));
      } else if (res && "error" in res) {
        console.error(res.error);
        setErrorMessage(t("No se pudo autorizar. Intenta nuevamente."));
      } else if (code) {
        const back = new URL(request.callbackUrl.toString());
        back.searchParams.set("state", request.state);
        back.searchParams.set("code", code);
        window.location.assign(back.toString());
        return; // navegando fuera del portal
      } else {
        setErrorMessage(t("Respuesta inesperada del servidor"));
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(t("No se pudo autorizar. Intenta nuevamente."));
    }
    setAuthorizing(false);
  };

  // Quien tiene abierta otra cuenta en el navegador (operador, holding) no
  // debería tener que reiniciar la conexión desde la tienda: se guarda esta
  // autorización ANTES de cerrar la sesión y el login lo trae de vuelta al
  // entrar con la cuenta del comercio. El cierre es el del menú de usuario
  // (UserMenu): la recarga completa descarta la sesión anterior del contexto
  // de auth.
  const switchAccount = () => {
    rememberConnectReturn(window.location);
    AuthService.logout();
    window.location.replace("/login");
  };

  const platformMeta = request.error ? null : PLATFORMS[request.platform];
  // Shopify no es un plugin dentro de la tienda: la clave la recibe nuestra app
  // embebida, y el comercio todavía tiene que encender el envío de pedidos en
  // ella. Los textos de Woo/PrestaShop no aplican tal cual.
  const isShopify = request.platform === "shopify";

  return (
    <div className="mx-auto w-full max-w-lg py-10">
      <div className="rounded-xl border border-border bg-bg p-6 shadow-sm">
        {request.error ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <AlertTriangle className="h-10 w-10 text-status-warning" aria-hidden="true" />
            <h1 className="text-lg font-semibold text-fg">{t("Solicitud de conexión inválida")}</h1>
            <p className="text-sm text-fg-muted">{request.error}</p>
            <Button variant="outline" onClick={() => navigate("/home/integrations")}>
              {t("Ir a Integraciones")}
            </Button>
          </div>
        ) : isLoading || !user ? (
          // Sesión resolviéndose (o camino a /login, que recuerda este retorno):
          // no se adelanta ni el aviso de cuenta equivocada ni el botón.
          <div className="flex justify-center py-6" role="status">
            <Loader2 className="h-6 w-6 animate-spin text-fg-muted" aria-hidden="true" />
            <span className="sr-only">{t("Cargando...")}</span>
          </div>
        ) : !isCommerceAccount(user) ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <LogIn className="h-10 w-10 text-fg-muted" aria-hidden="true" />
            <h1 className="text-lg font-semibold text-fg">
              {t("Inicia sesión con la cuenta del comercio")}
            </h1>
            <p className="text-sm text-fg-muted">
              {t(
                "Solo una cuenta de comercio puede autorizar la conexión de {{host}}. Cambia a la cuenta del comercio: al ingresar volverás a esta autorización.",
                { host: request.shopUrl.host },
              )}
            </p>
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
              <Button variant="outline" onClick={() => navigate("/")}>
                {t("Ir al inicio")}
              </Button>
              <Button
                variant="primary"
                onClick={switchAccount}
                iconLeft={<LogIn className="h-4 w-4" aria-hidden="true" />}
              >
                {t("Cambiar de cuenta")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="mig-logo" aria-hidden="true">
              <img
                src={iconList[platformMeta.icon]}
                alt=""
                className="mig-logo__img dark-safe-img"
              />
            </span>
            <h1 className="text-lg font-semibold text-fg">
              {t("Conectar tienda {{platform}}", { platform: platformMeta.label })}
            </h1>
            <p className="flex items-center gap-2 rounded-lg bg-bg-muted px-3 py-2 font-mono text-sm text-fg">
              <Store className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
              {request.shopUrl.host}
            </p>
            <p className="text-sm text-fg-muted">
              {t(
                "Esta tienda quiere vincularse a tu cuenta Beartrack{{company}}. Al autorizar, la tienda recibirá su clave de conexión y comenzará a sincronizar pedidos según tus reglas.",
                { company: user?.company?.name ? ` (${user.company.name})` : "" },
              )}
            </p>
            <p className="font-mono text-xs text-fg-subtle">
              {t("La clave se entregará a {{host}}", {
                host: request.callbackUrl.host,
              })}
            </p>

            {errorMessage && (
              <p className="text-sm text-status-error" role="alert">
                {errorMessage}
              </p>
            )}

            <label className="flex w-full items-start gap-2 rounded-lg border border-border bg-bg-muted px-3 py-2 text-left text-sm text-fg">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0"
                checked={hostConfirmed}
                onChange={(e) => setHostConfirmed(e.target.checked)}
                disabled={authorizing}
              />
              <span>
                {t(
                  "Confirmo que {{host}} es mi tienda. Si no reconoces ese dominio, cancela: quien lo controle podrá crear y consultar pedidos a nombre de tu cuenta.",
                  { host: request.shopUrl.host },
                )}
              </span>
            </label>

            {isShopify && (
              <p className="w-full rounded-lg border border-border px-3 py-2 text-left text-sm text-fg-muted">
                {t(
                  "Después de autorizar, vuelve a la app de Beartrack en tu admin de Shopify y activa \"Estado de la integración\": hasta entonces no se envía ningún pedido.",
                )}
              </p>
            )}

            <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
              <Button
                variant="outline"
                onClick={() => navigate("/home/integrations")}
                disabled={authorizing}
              >
                {t("Cancelar")}
              </Button>
              <Button
                variant="primary"
                onClick={authorize}
                loading={authorizing}
                disabled={!hostConfirmed}
                iconLeft={
                  authorizing ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Plug className="h-4 w-4" aria-hidden="true" />
                  )
                }
              >
                {t("Autorizar conexión")}
              </Button>
            </div>

            <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              {isShopify
                ? t("La clave se entrega directo a la app de Beartrack en Shopify; nunca viaja por el navegador.")
                : t("La clave se entrega directo a la tienda; nunca viaja por el navegador.")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
